import type { ReactNode } from "react";
import type { SnippetCategoryKey } from "routes/SnippetsPage/categories";

export type SnippetKind = "leaf" | "composite";

export type SnippetDefinition = {
  /** 採番ルール: LEAF-NNNN (ui 配下) / COMP-NNNN (composite) */
  id: string;
  name: string;
  kind: SnippetKind;
  category: SnippetCategoryKey;
  /** リポジトリルートからの相対パス */
  source: string;
  /** import パス (baseUrl=src 基準)。usage 収集にも使う */
  importPath: string;
  description: string;
  /** 全バリアントを描画する関数 */
  render: () => ReactNode;
};

/**
 * レジストリ本体は SnippetsPage.tsx から直接定義するのではなく、
 * 循環参照を避けるため data のみを type で定義し、renderer 側は別ファイルに持つ。
 *
 * 実際の定義は `routes/SnippetsPage/renderers.tsx` で行う。
 * このファイルは型定義とビルド時生成スクリプトから参照される静的データのみ。
 */
export type SnippetMetadata = Omit<SnippetDefinition, "render">;

/**
 * ビルド時生成スクリプト (`scripts/generate-snippet-usage.js`) は、
 * このメタデータ配列を JSON にシリアライズしたもの
 * (`routes/SnippetsPage/snippets.meta.json`) を読み込んで usage を収集する。
 */
export const snippetMetadata: SnippetMetadata[] = [
  // Leaf: ui/
  {
    id: "LEAF-0001",
    name: "Button",
    kind: "leaf",
    category: "form",
    source: "frontend/src/components/ui/Button/Button.tsx",
    importPath: "components/ui/Button",
    description:
      "共通 Button。variant × size × isOutline × width の組み合わせを持つ。",
  },
  {
    id: "LEAF-0002",
    name: "Badge",
    kind: "leaf",
    category: "display",
    source: "frontend/src/components/ui/Badge/Badge.tsx",
    importPath: "components/ui/Badge",
    description: "DaisyUI ベースのバッジ。color / size / isOutline を持つ。",
  },
  {
    id: "LEAF-0003",
    name: "Input",
    kind: "leaf",
    category: "form",
    source: "frontend/src/components/ui/Input/Input.tsx",
    importPath: "components/ui/Input",
    description: "テキスト入力。size × tone (default/invalid)。",
  },
  {
    id: "LEAF-0004",
    name: "Textarea",
    kind: "leaf",
    category: "form",
    source: "frontend/src/components/ui/Textarea/Textarea.tsx",
    importPath: "components/ui/Textarea",
    description: "複数行入力。size × tone。",
  },
  {
    id: "LEAF-0005",
    name: "Toggle",
    kind: "leaf",
    category: "form",
    source: "frontend/src/components/ui/Toggle/Toggle.tsx",
    importPath: "components/ui/Toggle",
    description: "チェックボックスをトグル表示。size × color × label。",
  },
  {
    id: "LEAF-0006",
    name: "Select",
    kind: "leaf",
    category: "form",
    source: "frontend/src/components/ui/Select/Select.tsx",
    importPath: "components/ui/Select",
    description: "react-select ラッパー。width × isDisabled。",
  },
  {
    id: "LEAF-0007",
    name: "RadioGroup",
    kind: "leaf",
    category: "form",
    source: "frontend/src/components/ui/RadioGroup/RadioGroup.tsx",
    importPath: "components/ui/RadioGroup",
    description: "ラジオグループ。orientation (horizontal/vertical)。",
  },
  {
    id: "LEAF-0008",
    name: "FieldWrapper",
    kind: "leaf",
    category: "form",
    source: "frontend/src/components/ui/FieldWrapper/FieldWrapper.tsx",
    importPath: "components/ui/FieldWrapper",
    description:
      "フォームフィールドの共通器。label/required/hint/error を統一描画。",
  },
  {
    id: "LEAF-0009",
    name: "DateTimeInput",
    kind: "leaf",
    category: "form",
    source: "frontend/src/components/ui/DateTimeInput/DateTimeInput.tsx",
    importPath: "components/ui/DateTimeInput",
    description: "datetime-local 入力のラッパー。",
  },
  {
    id: "LEAF-0010",
    name: "FromToRange",
    kind: "leaf",
    category: "form",
    source: "frontend/src/components/ui/FromToRange/FromToRange.tsx",
    importPath: "components/ui/FromToRange",
    description: "from / to 2本のスライダー。size × color。",
  },
  {
    id: "LEAF-0011",
    name: "CreatableMultiSelect",
    kind: "leaf",
    category: "form",
    source:
      "frontend/src/components/ui/CreatableMultiSelect/CreatableMultiSelect.tsx",
    importPath: "components/ui/CreatableMultiSelect",
    description: "タグ入力 (string[])。width × placeholder。",
  },
  {
    id: "LEAF-0012",
    name: "Link",
    kind: "leaf",
    category: "navigation",
    source: "frontend/src/components/ui/Link/Link.tsx",
    importPath: "components/ui/Link",
    description:
      "共通 Link リーフ。内部 (to) / 外部 (href) 両対応。色は text-violet-600 を default とし、CRM タレント列と統一する。",
  },
  {
    id: "LEAF-0014",
    name: "Loading",
    kind: "leaf",
    category: "feedback",
    source: "frontend/src/components/ui/Loading/Loading.tsx",
    importPath: "components/ui/Loading",
    description:
      "全画面で統一したい Grid (キューブ) 型ローディング。size (xs/sm/md/lg/xl) と color のみ露出。",
  },
  {
    id: "LEAF-0013",
    name: "DataTable",
    kind: "leaf",
    category: "display",
    source: "frontend/src/components/ui/Table/DataTable.tsx",
    importPath: "components/ui/Table",
    description:
      "全部入り DataTable。TanStack Table をエンジンに、sort / pagination / loading / empty を props で切替。primitives (Table.Root 等) も同階層に存在。",
  },
  // Composite: components/
  {
    id: "COMP-0001",
    name: "PageTitle",
    kind: "composite",
    category: "layout",
    source: "frontend/src/components/PageTitle/PageTitle.tsx",
    importPath: "components/PageTitle",
    description: "ページタイトル (h1)。",
  },
  {
    id: "COMP-0002",
    name: "ContentBoxUI",
    kind: "composite",
    category: "layout",
    source: "frontend/src/components/ContentBox/ContentBox.tsx",
    importPath: "components/ContentBox",
    description: "card 枠。title と子要素を表示する。",
  },
  {
    id: "COMP-0003",
    name: "FormGroupUI",
    kind: "composite",
    category: "layout",
    source: "frontend/src/components/FormGroup/FormGroup.tsx",
    importPath: "components/FormGroup",
    description: "縦積みフォームグループコンテナ。",
  },
  {
    id: "COMP-0004",
    name: "FormGroupLabelUI",
    kind: "composite",
    category: "display",
    source: "frontend/src/components/FormGroupLabel/FormGroupLabel.tsx",
    importPath: "components/FormGroupLabel",
    description: "FormGroup 内の見出しラベル。",
  },
  {
    id: "COMP-0005",
    name: "DismissibleAlert",
    kind: "composite",
    category: "feedback",
    source: "frontend/src/components/DismissibleAlert/DismissibleAlert.tsx",
    importPath: "components/DismissibleAlert",
    description: "閉じるボタン付きアラート。variant × 4。",
  },
  {
    id: "COMP-0006",
    name: "LoadingUI",
    kind: "composite",
    category: "feedback",
    source: "frontend/src/components/Loading/Loading.tsx",
    importPath: "components/Loading",
    description: "スピナー。type / color / width / height。",
  },
  {
    id: "COMP-0007",
    name: "BreadcrumbUI",
    kind: "composite",
    category: "navigation",
    source: "frontend/src/components/Breadcrumb/Breadcrumb.tsx",
    importPath: "components/Breadcrumb",
    description: "パンくず。BreadcrumbContext から items を取得して描画する。",
  },
];
